/*
 * Wemond "Sizden Gelenler": single-review rotator + Judge.me write-review trigger.
 * Shared by the homepage editorial band and the product-page review section.
 *
 *   [data-wr-rotator]          root; data-wr-interval (ms, 0 = manual only)
 *     [data-wr-slide]          one review; the active one has .is-active
 *     [data-wr-prev] / [data-wr-next] / [data-wr-index]
 *   [data-wr-write]            opens Judge.me's own write-review form
 *
 * Idempotent per element; instances are destroyed when their Shopify section unloads
 * or when their root leaves the document, so editor re-renders never stack timers.
 */
(() => {
  if (window.WemondReviews) {
    window.WemondReviews.scan(document);
    return;
  }

  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
  const instances = new Set();

  const isKeyboardFocus = (node) => {
    try { return node.matches(':focus-visible'); } catch (_) { return true; }
  };

  function createRotator(root) {
    const slides = [...root.querySelectorAll('[data-wr-slide]')];
    if (slides.length < 2) return null;

    const stage = root.querySelector('[data-wr-stage]');
    const counter = root.querySelector('[data-wr-index]');
    const interval = Math.max(0, Number(root.dataset.wrInterval) || 0);
    const pauses = new Set();
    const cleanups = [];
    let index = Math.max(0, slides.findIndex((slide) => slide.classList.contains('is-active')));
    let timer = 0;
    let inView = true;

    const listen = (target, type, handler, options) => {
      target.addEventListener(type, handler, options);
      cleanups.push(() => target.removeEventListener(type, handler, options));
    };

    const render = () => {
      slides.forEach((slide, i) => {
        const active = i === index;
        slide.classList.toggle('is-active', active);
        slide.setAttribute('aria-hidden', active ? 'false' : 'true');
      });
      if (counter) counter.textContent = String(index + 1);
      if (!slides[index].querySelector('details[open]')) pauses.delete('reply');
    };

    const stop = () => {
      window.clearTimeout(timer);
      timer = 0;
    };

    const schedule = () => {
      stop();
      if (!interval || reduced.matches || pauses.size || document.hidden || !inView) return;
      timer = window.setTimeout(() => {
        if (!root.isConnected) { destroy(); return; }
        index = (index + 1) % slides.length;
        render();
        schedule();
      }, interval);
    };

    const go = (step) => {
      index = (index + step + slides.length) % slides.length;
      if (stage) stage.setAttribute('aria-live', 'polite');
      render();
      schedule();
    };

    const pause = (reason) => { pauses.add(reason); stop(); };
    const resume = (reason) => { if (pauses.delete(reason)) schedule(); };

    const prev = root.querySelector('[data-wr-prev]');
    const next = root.querySelector('[data-wr-next]');
    if (prev) listen(prev, 'click', () => go(-1));
    if (next) listen(next, 'click', () => go(1));

    listen(root, 'mouseenter', () => pause('hover'));
    listen(root, 'mouseleave', () => resume('hover'));
    listen(root, 'focusin', (event) => { if (isKeyboardFocus(event.target)) pause('focus'); });
    listen(root, 'focusout', (event) => { if (!root.contains(event.relatedTarget)) resume('focus'); });
    listen(root, 'touchstart', () => pause('touch'), { passive: true });
    listen(root, 'touchend', () => resume('touch'), { passive: true });
    listen(root, 'touchcancel', () => resume('touch'), { passive: true });
    /* An expanded "Wemond yanıtı" is being read: hold the current review. */
    listen(root, 'toggle', () => {
      if (root.querySelector('[data-wr-slide].is-active details[open]')) pause('reply'); else resume('reply');
    }, true);
    listen(document, 'visibilitychange', () => (document.hidden ? stop() : schedule()));
    const onMotion = () => schedule();
    reduced.addEventListener?.('change', onMotion);
    cleanups.push(() => reduced.removeEventListener?.('change', onMotion));

    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver((entries) => {
        inView = entries.some((entry) => entry.isIntersecting);
        if (inView) schedule(); else stop();
      });
      observer.observe(root);
      cleanups.push(() => observer.disconnect());
    }

    render();
    schedule();

    function destroy() {
      stop();
      cleanups.splice(0).forEach((fn) => fn());
      delete root.dataset.wrReady;
      instances.delete(instance);
    }

    const instance = { root, destroy };
    return instance;
  }

  function scan(scope) {
    scope.querySelectorAll('[data-wr-rotator]').forEach((root) => {
      if (root.dataset.wrReady) return;
      root.dataset.wrReady = 'true';
      const instance = createRotator(root);
      if (instance) instances.add(instance);
    });
  }

  const findWriteButton = (scope) =>
    scope?.querySelector('.jm-action-buttons__button.jm-button--primary, .jdgm-write-rev-link');

  const waitFor = (fn, timeout) => new Promise((resolve) => {
    const started = Date.now();
    const tick = () => {
      const found = fn();
      if (found || Date.now() - started > timeout) resolve(found || null);
      else window.setTimeout(tick, 120);
    };
    tick();
  });

  /* Review photos open in a page dialog instead of linking out to Judge.me's image host. */
  let photoDialog = null;
  const openPhoto = (src, alt) => {
    if (!photoDialog) {
      photoDialog = document.createElement('dialog');
      photoDialog.className = 'wms-photo-dialog';
      photoDialog.innerHTML = '<button type="button" class="wms-photo-dialog__close" aria-label="Kapat">×</button><img alt="">';
      photoDialog.addEventListener('click', (event) => {
        if (event.target === photoDialog || event.target.closest('.wms-photo-dialog__close')) photoDialog.close();
      });
      document.body.appendChild(photoDialog);
    }
    const img = photoDialog.querySelector('img');
    img.src = src;
    img.alt = alt || '';
    photoDialog.showModal();
  };

  /* Delegated triggers survive section re-renders without re-binding. */
  document.addEventListener('click', async (event) => {
    const photoTrigger = event.target.closest('[data-wr-photo]');
    if (photoTrigger) {
      event.preventDefault();
      openPhoto(photoTrigger.dataset.wrPhoto, photoTrigger.querySelector('img')?.alt);
      return;
    }

    const writeTrigger = event.target.closest('[data-wr-write]');
    if (writeTrigger) {
      event.preventDefault();
      const scope = document.getElementById(writeTrigger.dataset.wrWrite);
      let button = findWriteButton(scope);
      if (!button) {
        writeTrigger.setAttribute('aria-busy', 'true');
        button = await waitFor(() => findWriteButton(scope), 8000);
        writeTrigger.removeAttribute('aria-busy');
      }
      if (button) button.click();
    }
  });

  const destroyWhere = (predicate) => {
    [...instances].forEach((instance) => { if (predicate(instance.root)) instance.destroy(); });
  };

  document.addEventListener('shopify:section:unload', (event) => {
    destroyWhere((root) => !root.isConnected || event.target.contains(root));
  });
  document.addEventListener('shopify:section:load', (event) => {
    destroyWhere((root) => !root.isConnected);
    scan(event.target);
  });

  window.WemondReviews = { scan };
  scan(document);
})();
